import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  InputNumber,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { EditOutlined, PlusOutlined, QrcodeOutlined } from "@ant-design/icons";
import ImageChecker from "../../Generic/ImageChecker";
import QRcomponent from "../../packages/PackageQRScanner/qrcomponent";

import "./styles.scss";

const { Option } = Select;
const { Text } = Typography;

const Index = ({
  visible,
  branchitems,
  itemcount,
  onIncrementProduct,
  onSetProductQuantity,
  onClose,
}) => {
  const [scanMode, setScanMode] = useState("increment");
  const [scanLoading, setScanLoading] = useState(false);
  const [cameraID, setCameraID] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [scannerSessionKey, setScannerSessionKey] = useState(0);
  const [scannedItem, setScannedItem] = useState(null);
  const [scannedQuantity, setScannedQuantity] = useState(1);
  const lastScanRef = useRef({ text: "", at: 0 });
  const lastScanMessageRef = useRef({ text: "", at: 0 });
  const scannerReadyAtRef = useRef(0);
  const scannerSessionRef = useRef(0);
  const scanRequestRef = useRef(0);

  useEffect(() => {
    const mediaDevices =
      typeof navigator !== "undefined" ? navigator.mediaDevices : null;
    const enumerateDevices = mediaDevices?.enumerateDevices;

    if (!visible || typeof enumerateDevices !== "function") {
      setCameras([]);
      setCameraID(null);
      return;
    }

    enumerateDevices
      .call(mediaDevices)
      .then((devices) => {
        const videoInputs = devices.filter(
          (device) => device.kind === "videoinput"
        );
        setCameras(videoInputs);
        if (!cameraID && videoInputs.length > 0) {
          setCameraID(videoInputs[0].deviceId);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, [visible, cameraID]);

  useEffect(() => {
    if (!scanLoading) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setScanLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [scanLoading]);

  useEffect(() => {
    if (!visible) {
      scannerSessionRef.current += 1;
      scanRequestRef.current += 1;
      lastScanRef.current = { text: "", at: 0 };
      lastScanMessageRef.current = { text: "", at: 0 };
      scannerReadyAtRef.current = 0;
      setScanLoading(false);
      setScanMode("increment");
      setScannedItem(null);
      setScannedQuantity(1);
      return;
    }

    scannerSessionRef.current += 1;
    scanRequestRef.current += 1;
    lastScanRef.current = { text: "", at: 0 };
    lastScanMessageRef.current = { text: "", at: 0 };
    scannerReadyAtRef.current = Date.now() + 700;
    setScannerSessionKey((prev) => prev + 1);
  }, [visible]);

  const findAuditableItemById = (productId) => {
    const inventoryMatch = (branchitems?.inventory || []).find(
      (item) => item.id === productId
    );

    if (inventoryMatch) {
      return inventoryMatch;
    }

    return (branchitems?.premix || []).find((item) => item.id === productId);
  };

  const handleScan = (event) => {
    if (!visible || !event?.text || scanLoading) {
      return;
    }

    const sessionId = scannerSessionRef.current;
    const requestId = scanRequestRef.current + 1;
    scanRequestRef.current = requestId;

    const text = event.text.trim();
    const now = Date.now();
    if (now < scannerReadyAtRef.current) {
      return;
    }

    const lastScan = lastScanRef.current;
    const lastMessage = lastScanMessageRef.current;

    if (!text.startsWith("prod_")) {
      if (lastMessage.text !== text || now - lastMessage.at > 1000) {
        lastScanMessageRef.current = { text, at: now };
        message.warning("Invalid product code. Expected a code starting with prod_.");
      }
      return;
    }

    if (lastScan.text === text && now - lastScan.at < 1000) {
      return;
    }

    const productId = text.slice(5);
    if (!productId) {
      if (lastMessage.text !== text || now - lastMessage.at > 1000) {
        lastScanMessageRef.current = { text, at: now };
        message.warning("Invalid product code payload.");
      }
      return;
    }

    lastScanRef.current = { text, at: now };
    lastScanMessageRef.current = { text, at: now };
    setScanLoading(true);

    const matchedItem = findAuditableItemById(productId);

    if (
      !visible ||
      sessionId !== scannerSessionRef.current ||
      requestId !== scanRequestRef.current
    ) {
      return;
    }

    if (!matchedItem) {
      message.warning("Scanned product is not part of this branch audit.");
      return;
    }

    if (scanMode === "increment") {
      const nextValue = onIncrementProduct(matchedItem);
      setScannedItem(matchedItem);
      setScannedQuantity(nextValue);
      message.success(`${matchedItem.name} +1`);
      return;
    }

    setScannedItem(matchedItem);
    setScannedQuantity(Number(itemcount?.[matchedItem.id] || 1));
  };

  const applyScannedQuantity = () => {
    if (!scannedItem) {
      message.warning("Scan a product first.");
      return;
    }

    onSetProductQuantity(scannedItem.id, scannedQuantity);
    message.success(`Set ${scannedItem.name} to ${scannedQuantity}.`);
    setScannedItem(null);
    setScannedQuantity(1);
  };

  const currentInputCount = scannedItem
    ? Number(itemcount?.[scannedItem.id] || 0)
    : 0;

  return (
    <Modal
      visible={visible}
      title="Scan Audit Items"
      onCancel={onClose}
      footer={null}
      destroyOnClose
      className="audit-scan-modal-shell"
    >
      <div className="audit-scan-modal">
        <div className="audit-scan-toolbar">
          <div className="audit-scan-toolbar-copy">
            <Text strong>Default: Scan +1</Text>
            <Text type="secondary">
              Scan repeatedly for fast counting, or switch to quantity mode when
              you want to scan first then set the total.
            </Text>
          </div>
          <Space wrap>
            <Button
              type={scanMode === "increment" ? "primary" : "default"}
              icon={<PlusOutlined />}
              onClick={() => setScanMode("increment")}
            >
              Scan +1
            </Button>
            <Button
              type={scanMode === "quantity" ? "primary" : "default"}
              icon={<EditOutlined />}
              onClick={() => setScanMode("quantity")}
            >
              Set Qty
            </Button>
          </Space>
        </div>

        {cameras.length > 1 ? (
          <Select
            value={cameraID}
            onChange={setCameraID}
            style={{ width: "100%" }}
          >
            {cameras.map((camera, index) => (
              <Option key={camera.deviceId} value={camera.deviceId}>
                {camera.label || `Camera ${index + 1}`}
              </Option>
            ))}
          </Select>
        ) : null}

        <div className="audit-scanner-frame">
          <QRcomponent
            key={scannerSessionKey}
            cameraID={cameraID}
            onScan={handleScan}
          />
        </div>

        <div className="audit-scan-mode-hint">
          <Tag color={scanMode === "increment" ? "green" : "blue"}>
            {scanMode === "increment" ? "Scan +1 Active" : "Set Qty Active"}
          </Tag>
          <Text type="secondary">
            {scanMode === "increment"
              ? "Each scan adds 1 to the main unit count."
              : "Scan an item, enter the counted quantity, then apply it."}
          </Text>
        </div>

        {scannedItem ? (
          <div className="audit-scan-result">
            <div className="audit-scan-result-copy">
              <ImageChecker
                url={`${process.env.REACT_APP_AWS_S3}${scannedItem.id}.png`}
                success={
                  <img
                    src={`${process.env.REACT_APP_AWS_S3}${scannedItem.id}.png`}
                    alt="product"
                    className="audit-scan-thumb"
                  />
                }
                fail={
                  <img
                    src={`${process.env.REACT_APP_AWS_S3}logo192.png`}
                    alt="product"
                    className="audit-scan-thumb"
                  />
                }
              />
              <div className="audit-scan-result-text">
                <Text strong>{scannedItem.name}</Text>
                <Text type="secondary">
                  Current input: {currentInputCount} {scannedItem.unit || ""}
                </Text>
              </div>
            </div>

            {scanMode === "quantity" ? (
              <div className="audit-scan-quantity-row">
                <InputNumber
                  min={0}
                  value={scannedQuantity}
                  onChange={(value) => setScannedQuantity(Number(value || 0))}
                />
                <Button type="primary" onClick={applyScannedQuantity}>
                  Apply Qty
                </Button>
                <Button
                  onClick={() => {
                    const nextValue = currentInputCount + 1;
                    setScannedQuantity(nextValue);
                    onSetProductQuantity(scannedItem.id, nextValue);
                  }}
                >
                  +1
                </Button>
              </div>
            ) : (
              <div className="audit-scan-quick-row">
                <Text type="secondary">Keep scanning this item to continue counting.</Text>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => setScanMode("quantity")}
                >
                  Switch To Set Qty
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="audit-scan-empty">
            <QrcodeOutlined />
            <Text type="secondary">Point the camera at a product QR code.</Text>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default Index;
